import { useEffect, useState } from 'preact/hooks'
import { AlertTriangle, FileText, Loader2, X } from 'lucide-preact'
import { websApi, type QuizDto, type WebQuizReportPreviewDto, type WebViewDto } from '@/api/webs'

interface Props {
    web: WebViewDto
    quiz: QuizDto
    onClose: () => void
}

const ACTIVE_BAND = 'font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
const INACTIVE_BAND = 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'

/**
 * The quiz report for made-up answers in one band, in a slide-over from the Quiz tab (ORB-922). The server scores the
 * sample and builds the same page the PDF is printed from, so what the author sees is the report, one long page
 * instead of A4 sheets. The page runs in a sandboxed frame, like the email preview, so its styles stay inside it.
 */
export function QuizReportPreview({ web, quiz, onClose }: Props) {
    const [requestedBandId, setRequestedBandId] = useState<string | undefined>(undefined)
    const [preview, setPreview] = useState<WebQuizReportPreviewDto | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const controller = new AbortController()
        setLoading(true)
        setError(null)
        websApi.getQuizReportPreview(web.WorkspaceId, web.Id, requestedBandId, controller.signal)
            .then(res => {
                if (controller.signal.aborted) return
                if (res.data && !res.error) setPreview(res.data)
                else setError(res.error || 'The report preview could not be built.')
            })
            .catch(() => {
                if (!controller.signal.aborted) setError('Network or server error')
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false)
            })
        return () => controller.abort()
    }, [web.WorkspaceId, web.Id, requestedBandId])

    useEffect(() => {
        const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
        document.addEventListener('keydown', closeOnEscape)
        return () => document.removeEventListener('keydown', closeOnEscape)
    }, [onClose])

    const activeBandId = requestedBandId ?? preview?.BandId

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black/50" aria-hidden="true" onClick={onClose} />
            <div id="report-preview" role="dialog" aria-modal="true" aria-labelledby="report-preview-title" className="absolute inset-y-0 right-0 w-full max-w-4xl h-full flex flex-col bg-white dark:bg-slate-900 shadow-xl">
                <div className="shrink-0 px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-6">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 shrink-0 rounded-full bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center">
                            <FileText className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div className="min-w-0">
                            <h2 id="report-preview-title" className="text-lg font-semibold text-slate-900 dark:text-white">Report preview</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Sample answers scored against the quiz as saved. Nothing is sent, and page breaks show only in the PDF.</p>
                        </div>
                    </div>
                    <button type="button" title="Close" onClick={onClose} className="shrink-0 p-2 rounded-lg text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div role="toolbar" aria-label="Band" className="shrink-0 flex items-center gap-1 px-4 py-1.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
                    <span className="mr-2 text-xs font-medium text-slate-500 dark:text-slate-400">Band</span>
                    {quiz.Bands.map(band => {
                        const active = band.Id === activeBandId
                        return (
                            <button
                                key={band.Id}
                                type="button"
                                aria-pressed={active}
                                onClick={() => setRequestedBandId(band.Id)}
                                className={`whitespace-nowrap px-2.5 py-1.5 rounded-md transition-colors text-xs flex items-center gap-1.5 ${active ? ACTIVE_BAND : INACTIVE_BAND}`}
                            >
                                {band.Name || band.Id}
                                <span className={active ? 'font-normal tabular-nums text-primary-700/70 dark:text-primary-300/70' : 'tabular-nums text-slate-400 dark:text-slate-500'}>{band.MinScore}-{band.MaxScore}</span>
                            </button>
                        )
                    })}
                    {preview && !error && (
                        <span className="ml-auto whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">
                            Sample score <span className="font-medium tabular-nums text-slate-700 dark:text-slate-200">{preview.Score}</span>
                        </span>
                    )}
                </div>

                {error && (
                    <div role="alert" className="shrink-0 flex items-start gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-300">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{error}</span>
                    </div>
                )}

                <div className="relative flex-1 min-h-0 flex justify-center bg-slate-100 dark:bg-slate-950 px-6 pt-6">
                    <div className="w-[794px] h-full overflow-hidden bg-white shadow-sm">
                        {preview && !error && (
                            <iframe title="Report preview" srcDoc={preview.Html} sandbox="" className="w-full h-full border-0 bg-white" />
                        )}
                    </div>
                    {loading && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
